import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation, useParams } from 'react-router-dom';
import { Cloud, Mail, Lock, AlertCircle, Globe2, Eye, EyeOff, Moon, Sun, MapPin, Globe, X, AlertTriangle, ChevronDown } from 'lucide-react';
import { useAuthStore } from '../stores/auth-store';
import { useI18n } from '../i18n';
import Captcha, { CaptchaHandle } from '../components/auth/Captcha';
import { useTenantOptional } from '../contexts/TenantContext';
import { TENANT_CONFIG, TenantId } from '../config/tenant';
import { supabase } from '../config/supabase';
import { withTimeout } from '../utils/with-timeout';
import '../styles/auth.css';

const LOGIN_SESSION_TIMEOUT_MS = 4000;

const getPostLoginPath = (state: unknown, tenant: TenantId) => {
  const from = (state as { from?: unknown } | null)?.from;
  if (
    typeof from === 'string'
    && from.startsWith('/')
    && !/^\/(?:cn|intl)\/(?:login|register)(?:\/|$)/.test(from)
  ) {
    return from;
  }
  return `/${tenant}/portal`;
};

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [showTenantModal, setShowTenantModal] = useState(false);
  const [loginMode, setLoginMode] = useState<'password' | 'magiclink'>('password');
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  const { signIn, signInWithOtp, verifyOtp } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { tenant: tenantParam } = useParams<{ tenant: string }>();
  const tenantContext = useTenantOptional();
  const { t, lang, switchTo } = useI18n();
  const [captchaValue, setCaptchaValue] = useState('');
  const [captchaError, setCaptchaError] = useState('');
  const captchaRef = useRef<CaptchaHandle | null>(null);

  // 获取当前租户
  const currentTenant: TenantId = (tenantParam === 'cn' || tenantParam === 'intl')
    ? tenantParam
    : (tenantContext?.tenant || 'cn');
  const postLoginPath = getPostLoginPath(location.state, currentTenant);

  useEffect(() => {
    let cancelled = false;

    const redirectAuthenticatedSession = (hasSession: boolean) => {
      if (!cancelled && hasSession) {
        navigate(postLoginPath, { replace: true });
        return true;
      }
      return false;
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      redirectAuthenticatedSession(Boolean(session));
    });

    void withTimeout(
      supabase.auth.getSession(),
      LOGIN_SESSION_TIMEOUT_MS,
      'Login page session check',
    )
      .then(({ data, error: sessionError }) => {
        if (cancelled) return;
        if (sessionError) {
          console.warn('登录页会话检查失败:', sessionError);
        }
        if (!redirectAuthenticatedSession(Boolean(data.session))) {
          setIsCheckingSession(false);
        }
      })
      .catch((sessionError) => {
        if (cancelled) return;
        console.warn('登录页会话检查超时:', sessionError);
        setIsCheckingSession(false);
      });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [navigate, postLoginPath]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // verify captcha first
      if (!captchaRef.current?.validate(captchaValue)) {
        setCaptchaError(t('captchaInvalid'));
        setIsLoading(false);
        return;
      }
      setCaptchaError('');
      await signIn(email, password);
      // 登录成功后重定向：优先返回来源页面，否则跳转到当前租户的门户
      navigate(postLoginPath, { replace: true });
    } catch {
      setError(t('invalidEmailOrPassword'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendOtp = async () => {
    if (!email.trim() || countdown > 0) return;
    setError('');
    setIsSendingOtp(true);
    try {
      await signInWithOtp(email);
      setMagicLinkSent(true);
      setCountdown(60);
    } catch (err: any) {
      const msg = err?.message || '';
      if (msg.includes('Signups not allowed') || msg.includes('not allowed')) {
        setError(t('emailNotRegistered'));
      } else {
        setError(t('magicLinkError'));
      }
    } finally {
      setIsSendingOtp(false);
    }
  };

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => setCountdown(c => c <= 1 ? 0 : c - 1), 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await verifyOtp(email, otpCode);
      navigate(postLoginPath, { replace: true });
    } catch {
      setError(t('otpVerifyError'));
    } finally {
      setIsLoading(false);
    }
  };

  // 切换到另一个区域
  const handleSwitchTenant = (targetTenant: TenantId) => {
    setShowTenantModal(false);
    if (targetTenant !== currentTenant) {
      navigate(`/${targetTenant}/login`);
    }
  };

  const isFormValid = loginMode === 'password'
    ? email.trim() && password.trim() && captchaRef.current?.validate(captchaValue)
    : email.trim() && otpCode.length === 6 && captchaRef.current?.validate(captchaValue);

  if (isCheckingSession) {
    return (
      <div className={`auth-background ${!isDarkMode ? 'light-mode' : ''}`}>
        <div className="min-h-screen flex items-center justify-center relative z-10">
          <div className="w-10 h-10 border-4 border-blue-400 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className={`auth-background ${!isDarkMode ? 'light-mode' : ''}`}>
      {/* Animated gradient orbs */}
      <div className="auth-orb auth-orb-1" />
      <div className="auth-orb auth-orb-2" />
      <div className="auth-orb auth-orb-3" />

      {/* Main content */}
      <div className="min-h-screen flex items-center justify-center p-4 relative z-10">
        <div className="auth-card auth-card-enter w-full max-w-[400px] p-10">
          {/* Logo & Title */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <div className="auth-icon-container">
                <Cloud strokeWidth={1.5} />
              </div>
            </div>
            {/* 租户标识 - 可点击切换 */}
            <div className="flex justify-center mb-4">
              <button
                type="button"
                onClick={() => setShowTenantModal(true)}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-gray-100 to-gray-50 rounded-full text-sm font-medium text-gray-700 border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all cursor-pointer"
              >
                <span className="text-lg">{TENANT_CONFIG[currentTenant].flag}</span>
                <span>{t(`tenant.${currentTenant}`)}</span>
                <ChevronDown size={14} className="text-gray-400" />
              </button>
            </div>
            <h1 className="auth-title">{t('welcomeBack')}</h1>
            <p className="auth-subtitle">
              {t('noAccount')}{' '}
              <Link to={`/${currentTenant}/register`} className="auth-link">
                {t('signUp')}
              </Link>
            </p>
          </div>

          {/* Login Mode Toggle */}
          <div className="flex items-center justify-center mb-6 p-1 bg-gray-100 rounded-xl">
            <button
              type="button"
              onClick={() => { setLoginMode('password'); setError(''); setMagicLinkSent(false); }}
              className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${loginMode === 'password'
                ? 'bg-white text-gray-800 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              <Lock size={14} className="inline mr-1.5 -mt-0.5" />
              {t('passwordLogin')}
            </button>
            <button
              type="button"
              onClick={() => { setLoginMode('magiclink'); setError(''); }}
              className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${loginMode === 'magiclink'
                ? 'bg-white text-gray-800 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              <Mail size={14} className="inline mr-1.5 -mt-0.5" />
              {t('magicLinkLogin')}
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="auth-error">
              <AlertCircle size={18} />
              <span>{error}</span>
            </div>
          )}

          {/* Password Login Form */}
          {loginMode === 'password' && (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email Field */}
              <div className="auth-input-group">
                <div className="relative">
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="auth-input"
                    placeholder={t('emailAddress')}
                    autoComplete="email"
                    required
                  />
                  <Mail size={18} className="auth-input-icon" />
                </div>
              </div>

              {/* Password Field */}
              <div className="auth-input-group">
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="auth-input"
                    placeholder={t('password')}
                    autoComplete="current-password"
                    required
                  />
                  <Lock size={18} className="auth-input-icon" />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="auth-input-action"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {/* Forgot Password Link */}
              <div className="flex justify-end -mt-2 mb-2">
                <Link to={`/${currentTenant}/reset-password`} className="auth-link text-sm">
                  {t('forgotPassword')}
                </Link>
              </div>

              {/* Captcha Field */}
              <div className="auth-input-group">
                <div className="auth-captcha-container">
                  <input
                    type="text"
                    value={captchaValue}
                    onChange={(e) => setCaptchaValue(e.target.value)}
                    placeholder={t('captchaPlaceholder')}
                    className="auth-captcha-input"
                  />
                  <div className="auth-captcha-wrapper">
                    <Captcha
                      ref={captchaRef}
                      onChange={() => {
                        setCaptchaError('');
                        setCaptchaValue('');
                      }}
                    />
                  </div>
                </div>
                {captchaError && (
                  <p className="mt-2 text-sm text-red-400">{captchaError}</p>
                )}
              </div>

              {/* Submit Button */}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={isLoading || !isFormValid}
                  className="auth-button-primary"
                >
                  <span className="flex items-center justify-center">
                    {isLoading && <span className="auth-spinner" />}
                    {isLoading ? t('signingIn') : t('signInAction')}
                  </span>
                </button>
              </div>
            </form>
          )}

          {/* Email OTP Login Form */}
          {loginMode === 'magiclink' && (
            <form onSubmit={handleVerifyOtp} className="space-y-4">
              {/* Email Field */}
              <div className="auth-input-group">
                <div className="relative">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="auth-input"
                    placeholder={t('emailAddress')}
                    autoComplete="email"
                    required
                  />
                  <Mail size={18} className="auth-input-icon" />
                </div>
              </div>

              {/* OTP Code Field */}
              <div className="auth-input-group">
                <div className="relative flex gap-2">
                  <div className="relative flex-1">
                    <input
                      type="text"
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="auth-input font-mono tracking-widest"
                      placeholder={t('otpPlaceholder')}
                      maxLength={6}
                      required
                    />
                    <Lock size={18} className="auth-input-icon" />
                  </div>
                  <button
                    type="button"
                    onClick={handleSendOtp}
                    disabled={isSendingOtp || !email.trim() || countdown > 0}
                    className="flex-shrink-0 px-4 py-2 text-sm font-medium rounded-xl transition-all whitespace-nowrap"
                    style={{
                      backgroundColor: (countdown > 0 || magicLinkSent) ? '#f5f5f7' : '#003cff',
                      color: (countdown > 0 || magicLinkSent) ? '#86868b' : '#ffffff',
                      border: (countdown > 0 || magicLinkSent) ? '1px solid #e8e8ed' : 'none',
                    }}
                  >
                    {isSendingOtp ? t('sendingMagicLink') : countdown > 0 ? `${t('otpResend')}(${countdown}s)` : magicLinkSent ? t('otpResend') : t('sendMagicLink')}
                  </button>
                </div>
              </div>

              {/* Captcha Field */}
              <div className="auth-input-group">
                <div className="auth-captcha-container">
                  <input
                    type="text"
                    value={captchaValue}
                    onChange={(e) => setCaptchaValue(e.target.value)}
                    placeholder={t('captchaPlaceholder')}
                    className="auth-captcha-input"
                  />
                  <div className="auth-captcha-wrapper">
                    <Captcha
                      ref={captchaRef}
                      onChange={() => {
                        setCaptchaError('');
                        setCaptchaValue('');
                      }}
                    />
                  </div>
                </div>
                {captchaError && (
                  <p className="mt-2 text-sm text-red-400">{captchaError}</p>
                )}
              </div>

              {/* Submit Button */}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={isLoading || !isFormValid}
                  className="auth-button-primary"
                >
                  <span className="flex items-center justify-center">
                    {isLoading && <span className="auth-spinner" />}
                    {isLoading ? t('signingIn') : t('signInAction')}
                  </span>
                </button>
              </div>
            </form>
          )}

          {/* Footer Controls */}
          <div className="auth-footer mt-8">
            <button
              type="button"
              onClick={() => setIsDarkMode(!isDarkMode)}
              className="auth-footer-button"
            >
              {isDarkMode ? <Sun size={14} /> : <Moon size={14} />}
            </button>
            <button
              type="button"
              onClick={() => switchTo(lang === 'zh' ? 'en' : 'zh')}
              className="auth-footer-button"
            >
              <Globe2 size={14} />
              {lang === 'zh' ? 'English' : '中文'}
            </button>
          </div>
        </div>
      </div>

      {/* 区域切换模态框 */}
      {showTenantModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-sm mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            {/* 标题 */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-800">
                {lang === 'zh' ? '选择服务区域' : 'Select Region'}
              </h3>
              <button
                onClick={() => setShowTenantModal(false)}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {/* 警告提示 */}
            <div className="mx-6 mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex gap-2">
                <AlertTriangle size={18} className="text-amber-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium">{lang === 'zh' ? '重要提示' : 'Important Notice'}</p>
                  <p className="mt-1 text-amber-700">
                    {lang === 'zh'
                      ? '国内与国际账户不互通，请根据您的实际所在区域选择。'
                      : 'Domestic and international accounts are separate. Please select based on your actual region.'}
                  </p>
                </div>
              </div>
            </div>

            {/* 区域选项 */}
            <div className="p-6 space-y-3">
              {/* 国内选项 */}
              <button
                onClick={() => handleSwitchTenant('cn')}
                className={`w-full flex items-center gap-4 p-4 border rounded-xl transition-all ${currentTenant === 'cn'
                  ? 'border-red-400 bg-red-50 ring-2 ring-red-200'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
              >
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${currentTenant === 'cn' ? 'bg-red-100' : 'bg-gray-100'
                  }`}>
                  <MapPin size={24} className={currentTenant === 'cn' ? 'text-red-500' : 'text-gray-500'} />
                </div>
                <div className="flex-1 text-left">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">🇨🇳</span>
                    <span className="font-semibold text-gray-800">{lang === 'zh' ? '国内' : 'China'}</span>
                    {currentTenant === 'cn' && (
                      <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                        {lang === 'zh' ? '当前' : 'Current'}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {lang === 'zh' ? '中国大陆地区用户' : 'Users in Mainland China'}
                  </p>
                </div>
              </button>

              {/* 国际选项 */}
              <button
                onClick={() => handleSwitchTenant('intl')}
                className={`w-full flex items-center gap-4 p-4 border rounded-xl transition-all ${currentTenant === 'intl'
                  ? 'border-blue-400 bg-blue-50 ring-2 ring-blue-200'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
              >
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${currentTenant === 'intl' ? 'bg-blue-100' : 'bg-gray-100'
                  }`}>
                  <Globe size={24} className={currentTenant === 'intl' ? 'text-blue-500' : 'text-gray-500'} />
                </div>
                <div className="flex-1 text-left">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">🌍</span>
                    <span className="font-semibold text-gray-800">{lang === 'zh' ? '国际' : 'International'}</span>
                    {currentTenant === 'intl' && (
                      <span className="px-2 py-0.5 bg-blue-500 text-white text-xs rounded-full">
                        {lang === 'zh' ? '当前' : 'Current'}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {lang === 'zh' ? '海外及港澳台地区用户' : 'Overseas, HK, Macau & Taiwan users'}
                  </p>
                </div>
              </button>
            </div>

            {/* 底部按钮 */}
            <div className="px-6 pb-6">
              <button
                onClick={() => setShowTenantModal(false)}
                className="w-full py-3 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                {lang === 'zh' ? '取消' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;
